import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';

const SLIDES = [
  { key: 'dashboard', img: '/screenshots/dashboard.jpg' },
  { key: 'transactions', img: '/screenshots/transactions.jpg' },
  { key: 'budget', img: '/screenshots/budget.jpg' },
  { key: 'portfolio', img: '/screenshots/portfolio.jpg' },
  { key: 'market', img: '/screenshots/market.jpg' },
  { key: 'reports', img: '/screenshots/reports.jpg' },
  { key: 'archive', img: '/screenshots/archive.jpg' },
  { key: 'settings', img: '/screenshots/settings.jpg' },
];

// ──────────────────────────────────────────────────────────────────────
// ScreenshotCarousel — diaporama des onglets du logiciel (landing page).
// Autoplay 4 s (pause au survol), flèches, points, légende par onglet.
// ──────────────────────────────────────────────────────────────────────
export const ScreenshotCarousel = () => {
  const { t } = useLanguage();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => setIndex((i) => (i + 1) % SLIDES.length), []);
  const prev = () => setIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setInterval(next, 4000);
    return () => clearInterval(timerRef.current);
  }, [paused, next]);

  const slide = SLIDES[index];

  return (
    <section className="py-20 px-4 bg-white" data-testid="landing-slideshow-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 border-2 border-emerald-300 text-emerald-800 text-xs font-bold uppercase tracking-wide mb-4">
            {t('landing.slidesBadge')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-black mb-3">
            {t('landing.slidesTitle')}
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto">{t('landing.slidesSubtitle')}</p>
        </div>

        <div
          className="relative rounded-xl overflow-hidden border-2 border-emerald-200 shadow-lg bg-white"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="aspect-video relative">
            {SLIDES.map((s, i) => (
              <img
                key={s.key}
                src={s.img}
                alt={t(`landing.slide_${s.key}`)}
                loading={i === 0 ? 'eager' : 'lazy'}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                  i === index ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
          </div>

          {/* Légende */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-emerald-950/90 to-transparent pt-12 pb-4 px-6 pointer-events-none">
            <p data-testid="slideshow-caption" className="text-white font-bold text-lg drop-shadow">
              {t(`landing.slide_${slide.key}`)}
            </p>
          </div>

          {/* Flèches */}
          <button
            type="button"
            data-testid="slideshow-prev"
            onClick={prev}
            aria-label="Previous"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white border-2 border-emerald-300 flex items-center justify-center shadow-lg transition-colors"
          >
            <CaretLeft size={20} weight="bold" className="text-emerald-800" />
          </button>
          <button
            type="button"
            data-testid="slideshow-next"
            onClick={next}
            aria-label="Next"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 hover:bg-white border-2 border-emerald-300 flex items-center justify-center shadow-lg transition-colors"
          >
            <CaretRight size={20} weight="bold" className="text-emerald-800" />
          </button>
        </div>

        {/* Points */}
        <div className="flex justify-center gap-2 mt-5">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              type="button"
              data-testid={`slideshow-dot-${s.key}`}
              onClick={() => setIndex(i)}
              aria-label={t(`landing.slide_${s.key}`)}
              className={`h-2.5 rounded-full transition-all ${
                i === index ? 'w-8 bg-emerald-700' : 'w-2.5 bg-emerald-200 hover:bg-emerald-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ScreenshotCarousel;
